/**
 * SEO Dynamic Page Component
 * Renders dynamically generated SEO content based on URL slug
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import HeaderOne from '../common/header/HeaderOne';
import FooterOne from '../common/footer/FooterOne';
import Breadcrumb from '../common/breadcrumb/Breadcrumb';
import { seoContentAgent, recipeAgent, seasonalProduceAgent } from '@/services/agents';
import linkingHelper from '@/utils/linkingHelper';
import { ChefHat, MapPin, Calendar, Leaf, ArrowRight, Search } from 'lucide-react';
import RecipeSidebar from '../components/sidebar/RecipeSidebar';
import ProduceSidebar from '../components/sidebar/ProduceSidebar';
import SeasonalGuideSidebar from '../components/sidebar/SeasonalGuideSidebar';
import RecipeDetails from '../components/recipe/RecipeDetails';
import contentAutoLinker from '@/utils/contentAutoLinker';

interface SEOPageData {
  slug: string;
  title: string;
  metaDescription: string;
  h1: string;
  content: string;
  keywords: string[];
  schema: any;
  relatedPages: string[];
  type: 'product' | 'location' | 'seasonal' | 'guide' | 'category' | 'recipe';
  // Additional data for sidebars
  recipeData?: any;
  produceData?: any;
  seasonalGuideData?: any;
  relatedContent?: any[];
}

interface SEOPageProps {
  pageType?: 'product' | 'location' | 'seasonal' | 'guide' | 'category' | 'recipe';
}

const SEOPage: React.FC<SEOPageProps> = ({ pageType: propType }) => {
  const { slug } = useParams<{ slug: string }>();
  const [pageData, setPageData] = useState<SEOPageData | null>(null);
  const [relatedRecipes, setRelatedRecipes] = useState<any[]>([]);
  const [seasonalInfo, setSeasonalInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      generatePageContent(slug, propType);
    }
  }, [slug, propType]);

  const generatePageContent = (slug: string, type?: SEOPageData['type']) => {
    setLoading(true);

    let data: SEOPageData | null = null;

    // Capitalize helper
    const capitalize = (str: string) => str.split(' ').map(w =>
      w.charAt(0).toUpperCase() + w.slice(1)
    ).join(' ');

    // Use explicit type if provided, otherwise parse slug
    if (type === 'guide' || (!type && slug.startsWith('guide-'))) {
      const topic = type ? slug.replace(/-/g, ' ') : slug.replace('guide-', '').replace(/-/g, ' ');
      const seoData = seoContentAgent.generateGuidePage(capitalize(topic), [
        'Getting Started',
        'Best Practices',
        'Tips and Tricks',
        'Common Questions'
      ]);
      data = { ...seoData, type: 'guide' };
    } else if (type === 'seasonal' || (!type && slug.startsWith('seasonal-'))) {
      const monthName = type ? slug : slug.replace('seasonal-', '');
      const monthIndex = [
        'january', 'february', 'march', 'april', 'may', 'june',
        'july', 'august', 'september', 'october', 'november', 'december'
      ].indexOf(monthName.toLowerCase()) + 1;

      if (monthIndex > 0) {
        const seoData = seoContentAgent.generateSeasonalPage(monthIndex);
        const seasonalRecs = seasonalProduceAgent.getSeasonalRecommendations(monthIndex);
        setSeasonalInfo(seasonalRecs);

        // Create seasonal guide data for sidebar
        const seasonalGuideData = {
          id: slug,
          name: monthName.charAt(0).toUpperCase() + monthName.slice(1),
          month: monthIndex,
          description: seoData.metaDescription,
          featuredProduce: seasonalRecs.peakProduce?.map((p: any) => p.name) || [],
        };

        data = {
          ...seoData,
          type: 'seasonal',
          seasonalGuideData: seasonalGuideData,
          relatedContent: [],
        };
      } else {
        // Default to current month if not recognized
        const currentMonth = new Date().getMonth() + 1;
        const seoData = seoContentAgent.generateSeasonalPage(currentMonth);
        const seasonalRecs = seasonalProduceAgent.getSeasonalRecommendations(currentMonth);
        setSeasonalInfo(seasonalRecs);

        // Create seasonal guide data for sidebar
        const seasonalGuideData = {
          id: 'current',
          name: 'Current Season',
          month: currentMonth,
          description: seoData.metaDescription,
          featuredProduce: seasonalRecs.peakProduce?.map((p: any) => p.name) || [],
        };

        data = {
          ...seoData,
          type: 'seasonal',
          seasonalGuideData: seasonalGuideData,
          relatedContent: [],
        };
      }
    } else if (type === 'location' || (!type && slug.startsWith('farms-'))) {
      const city = type ? slug.replace(/-/g, ' ') : slug.replace('farms-', '').replace(/-/g, ' ');
      const seoData = seoContentAgent.generateLocationPage(capitalize(city), 'farms');
      data = { ...seoData, type: 'location' };
    } else if (type === 'product') {
      // Product page - KEY for "Basil Example"
      const product = slug.replace(/-/g, ' ');
      const productName = capitalize(product);
      const seoData = seoContentAgent.generateProductPage(productName, 'SWFL');

      // Get related recipes for sidebar
      const recipes = recipeAgent.getRecipesByIngredients([productName], 6);
      setRelatedRecipes(recipes);

      // Create produce data for sidebar
      const produceData = {
        id: slug,
        name: productName,
        category: seoData.keywords.includes('herb') ? 'herb' :
                  seoData.keywords.includes('fruit') ? 'fruit' : 'vegetable',
        description: seoData.metaDescription,
        seasonalMonths: [], // Will be enhanced when we have actual data
        tags: seoData.keywords,
      };

      data = {
        ...seoData,
        type: 'product',
        produceData: produceData,
        relatedContent: [],
      };
    } else if (type === 'category') {
      const topic = slug.replace(/-/g, ' ');
      const seoData = seoContentAgent.generateComparisonPage(capitalize(topic));
      data = { ...seoData, type: 'category' };
    } else if (type === 'recipe') {
      // Recipe page
      const recipe = recipeAgent.getRecipeById(slug);
      if (recipe) {
        const recipeName = recipe.name || recipe.title || 'Recipe';

        // Get related recipes for sidebar
        const relatedRecipesList = recipeAgent.getRecipesByIngredients(recipe.primaryProduce || [], 5)
          .filter(r => r.id !== recipe.id)
          .map(r => ({
            id: r.id,
            title: r.name || r.title || '',
            description: r.description,
            url: linkingHelper.getRecipeUrl(r.id),
            type: 'recipe' as const,
            meta: `${r.difficulty} • ${r.prepTime} min`,
          }));

        data = {
          slug: slug,
          title: recipeName,
          metaDescription: recipe.description || '',
          h1: recipeName,
          content: recipe.description || '',
          keywords: recipe.primaryProduce || [],
          schema: {
            '@context': 'https://schema.org',
            '@type': 'Recipe',
            name: recipeName,
            description: recipe.description || '',
            prepTime: `PT${recipe.prepTime}M`,
            cookTime: `PT${recipe.cookTime}M`,
            recipeYield: recipe.servings,
            recipeIngredient: recipe.primaryProduce || [],
          },
          relatedPages: [],
          type: 'recipe',
          recipeData: recipe,
          relatedContent: relatedRecipesList,
        };

        setRelatedRecipes(relatedRecipesList);
      }
    } else {
      // Fallback: treat as product for old URLs or untyped
      const product = slug.replace(/-swfl$/, '').replace(/-/g, ' ');
      const seoData = seoContentAgent.generateProductPage(capitalize(product), 'SWFL');
      data = { ...seoData, type: 'product' };

      const recipes = recipeAgent.getRecipesByIngredients([capitalize(product)], 3);
      setRelatedRecipes(recipes);
    }

    if (data) {
      setPageData(data);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <>
        <HeaderOne />
        <div style={styles.loadingContainer}>
          <div style={styles.loadingSpinner} />
          <p>Loading content...</p>
        </div>
        <FooterOne />
      </>
    );
  }

  if (!pageData) {
    return (
      <>
        <HeaderOne />
        <Breadcrumb
          title="Page Not Found"
          currentPage="Error"
          img="/assets/images/backgrounds/breadcrumb-bg.jpg"
        />
        <div style={styles.errorContainer}>
          <h2>Content Not Found</h2>
          <p>The page you are looking for could not be generated.</p>
          <Link to="/browse" style={styles.backLink}>
            Browse All Farms
          </Link>
        </div>
        <FooterOne />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageData.title}</title>
        <meta name="description" content={pageData.metaDescription} />
        <meta name="keywords" content={pageData.keywords.join(', ')} />
        <script type="application/ld+json">
          {JSON.stringify(pageData.schema)}
        </script>
      </Helmet>

      <HeaderOne />
      <Breadcrumb
        title={pageData.h1}
        currentPage={
          pageData.type === 'product' ? 'Products' :
          pageData.type === 'recipe' ? 'Recipes' :
          pageData.type.charAt(0).toUpperCase() + pageData.type.slice(1)
        }
        img="/assets/images/backgrounds/breadcrumb-bg.jpg"
      />

      {/* Main Content Section */}
      <section style={styles.mainSection}>
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              {/* Page Content - with auto-linking */}
              <div style={styles.contentCard}>
                <div
                  style={styles.markdownContent}
                  dangerouslySetInnerHTML={{
                    __html: (() => {
                      // First, apply markdown transformations
                      let processedContent = pageData.content
                        .replace(/^## (.+)$/gm, '<h2 class="seo-h2">$1</h2>')
                        .replace(/^### (.+)$/gm, '<h3 class="seo-h3">$1</h3>')
                        .replace(/^- (.+)$/gm, '<li>$1</li>')
                        .replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>')
                        .replace(/<\/ul>\n<ul>/g, '')
                        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
                        .replace(/\n\n/g, '</p><p>')
                        .replace(/^(.+)$/gm, (match) => {
                          if (match.startsWith('<')) return match;
                          return `<p>${match}</p>`;
                        });

                      // Then, auto-link produce mentions for guide and location pages
                      if (pageData.type === 'guide' || pageData.type === 'location') {
                        processedContent = contentAutoLinker.autoLinkProduce(processedContent);
                      }

                      return processedContent;
                    })()
                  }}
                />
              </div>

              {/* Recipe Details Section for Recipe Pages - KEY for "Basil Example" */}
              {pageData.type === 'recipe' && pageData.recipeData && (
                <RecipeDetails recipe={pageData.recipeData} />
              )}

              {/* Related Recipes for Product and Recipe Pages */}
              {(pageData.type === 'product' || pageData.type === 'recipe') && relatedRecipes.length > 0 && (
                <div style={styles.relatedSection}>
                  <h3 style={styles.relatedTitle}>
                    <ChefHat size={20} style={{ marginRight: '8px' }} />
                    {pageData.type === 'recipe' ? 'Related Recipes' : 'Recipe Ideas'}
                  </h3>
                  <div style={styles.recipeGrid}>
                    {relatedRecipes.map((recipe, index) => (
                      <Link
                        key={index}
                        to={linkingHelper.getRecipeUrl(recipe.id || recipe.name)}
                        style={styles.recipeCardLink}
                      >
                        <div style={styles.recipeCard}>
                          <h4 style={styles.recipeName}>{recipe.name}</h4>
                          <p style={styles.recipeDesc}>{recipe.description}</p>
                          <div style={styles.recipeMeta}>
                            <span>{recipe.prepTime + recipe.cookTime} min</span>
                            <span>{recipe.difficulty}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Seasonal Info for Seasonal Pages */}
              {pageData.type === 'seasonal' && seasonalInfo && (
                <div style={styles.relatedSection}>
                  <h3 style={styles.relatedTitle}>
                    <Leaf size={20} style={{ marginRight: '8px' }} />
                    Peak Produce This Month
                  </h3>
                  <div style={styles.produceGrid}>
                    {seasonalInfo.peakProduce.slice(0, 6).map((produce: any, index: number) => (
                      <Link
                        key={index}
                        to={linkingHelper.getProductUrl(produce.name)}
                        style={styles.produceCard}
                      >
                        <span style={styles.produceEmoji}>
                          {produce.category === 'fruit' ? '🍎' :
                           produce.category === 'vegetable' ? '🥬' : '🌿'}
                        </span>
                        <span style={styles.produceName}>{produce.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="col-lg-4">
              {/* Conditional Sidebars based on page type */}
              {pageData.type === 'recipe' && pageData.recipeData && (
                <RecipeSidebar
                  recipe={pageData.recipeData}
                  relatedRecipes={pageData.relatedContent || []}
                  showFarmSources={true}
                  showSeasonalProduce={true}
                />
              )}

              {pageData.type === 'product' && pageData.produceData && (
                <ProduceSidebar
                  produce={pageData.produceData}
                  relatedProduce={pageData.relatedContent || []}
                  showRecipes={true}
                  showWhereToBuy={true}
                  showSeasonalCalendar={true}
                  showNutrition={true}
                />
              )}

              {pageData.type === 'seasonal' && pageData.seasonalGuideData && (
                <SeasonalGuideSidebar
                  guide={pageData.seasonalGuideData}
                  relatedGuides={pageData.relatedContent || []}
                  showFeaturedProduce={true}
                  showRecipes={true}
                  showFarms={true}
                />
              )}

              {/* Fallback sidebar for other page types */}
              {!['recipe', 'product', 'seasonal'].includes(pageData.type) && (
                <>
                  {/* Quick Actions */}
                  <div style={styles.sidebarCard}>
                    <h4 style={styles.sidebarTitle}>Quick Actions</h4>
                    <div style={styles.actionLinks}>
                      <Link to="/browse" style={styles.actionLink}>
                        <Search size={16} />
                        <span>Browse All Farms</span>
                        <ArrowRight size={14} />
                      </Link>
                      <Link to="/map" style={styles.actionLink}>
                        <MapPin size={16} />
                        <span>View Map</span>
                        <ArrowRight size={14} />
                      </Link>
                      <Link to="/seasonal" style={styles.actionLink}>
                        <Calendar size={16} />
                        <span>Seasonal Guide</span>
                        <ArrowRight size={14} />
                      </Link>
                      <Link to="/recipes" style={styles.actionLink}>
                        <ChefHat size={16} />
                        <span>Recipe Archive</span>
                        <ArrowRight size={14} />
                      </Link>
                      <Link to="/produce-directory" style={styles.actionLink}>
                        <Leaf size={16} />
                        <span>Produce Guide</span>
                        <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>

                  {/* Contextual Content Suggestions */}
                  {pageData.type === 'guide' && (
                    <>
                      <div style={styles.sidebarCard}>
                        <h4 style={styles.sidebarTitle}>Related Recipes</h4>
                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>
                          Explore recipes featuring local produce
                        </p>
                        <Link
                          to="/recipes"
                          style={{
                            ...styles.actionLink,
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '12px',
                            backgroundColor: '#f0f7ef',
                          }}
                        >
                          <span>Browse All Recipes</span>
                          <ArrowRight size={14} />
                        </Link>
                      </div>

                      <div style={styles.sidebarCard}>
                        <h4 style={styles.sidebarTitle}>Seasonal Guide</h4>
                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>
                          See what's in season this month
                        </p>
                        <Link
                          to="/seasonal"
                          style={{
                            ...styles.actionLink,
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '12px',
                            backgroundColor: '#f0f7ef',
                          }}
                        >
                          <span>View Seasonal Calendar</span>
                          <ArrowRight size={14} />
                        </Link>
                      </div>
                    </>
                  )}

                  {pageData.type === 'location' && (
                    <div style={styles.sidebarCard}>
                      <h4 style={styles.sidebarTitle}>Find Local Farms</h4>
                      <p style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>
                        Discover farms and markets in this area
                      </p>
                      <Link
                        to="/browse"
                        style={{
                          ...styles.actionLink,
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '12px',
                          backgroundColor: '#f0f7ef',
                        }}
                      >
                        <span>Browse Directory</span>
                        <ArrowRight size={14} />
                      </Link>
                      <Link
                        to="/map"
                        style={{
                          ...styles.actionLink,
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '12px',
                          backgroundColor: '#f0f7ef',
                          marginTop: '8px',
                        }}
                      >
                        <span>View on Map</span>
                        <ArrowRight size={14} />
                      </Link>
                    </div>
                  )}

                  {/* Related Pages */}
                  {pageData.relatedPages.length > 0 && (
                    <div style={styles.sidebarCard}>
                      <h4 style={styles.sidebarTitle}>Related Pages</h4>
                      <div style={styles.relatedLinks}>
                        {pageData.relatedPages.map((url, index) => (
                          <Link key={index} to={url} style={styles.relatedLink}>
                            {url.replace('/', '').replace(/-/g, ' ') || 'Home'}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Keywords */}
                  <div style={styles.sidebarCard}>
                    <h4 style={styles.sidebarTitle}>Related Searches</h4>
                    <div style={styles.keywordTags}>
                      {pageData.keywords.slice(0, 6).map((keyword, index) => (
                        <Link
                          key={index}
                          to={linkingHelper.getSearchUrl(keyword)}
                          style={styles.keywordTag}
                        >
                          {keyword}
                        </Link>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <FooterOne />
    </>
  );
};

const styles = {
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '100px 20px',
    textAlign: 'center' as const,
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #E8EDE7',
    borderTopColor: '#2D5A27',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px',
  },
  errorContainer: {
    textAlign: 'center' as const,
    padding: '80px 20px',
  },
  backLink: {
    display: 'inline-block',
    marginTop: '20px',
    padding: '12px 24px',
    backgroundColor: '#2D5A27',
    color: '#fff',
    borderRadius: '8px',
    textDecoration: 'none',
  },
  mainSection: {
    padding: '60px 0',
    backgroundColor: '#F9FAF8',
  },
  contentCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '32px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    marginBottom: '24px',
  },
  markdownContent: {
    fontSize: '16px',
    lineHeight: '1.8',
    color: '#333',
  },
  relatedSection: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    marginBottom: '24px',
  },
  relatedTitle: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '18px',
    fontWeight: '700' as const,
    color: '#1a1a1a',
    marginBottom: '20px',
  },
  recipeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '16px',
  },
  recipeCardLink: {
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    transition: 'transform 0.2s',
  },
  recipeCard: {
    padding: '16px',
    backgroundColor: '#F9FAF8',
    borderRadius: '12px',
    border: '1px solid #E8EDE7',
    transition: 'box-shadow 0.2s, transform 0.2s',
    cursor: 'pointer',
  },
  recipeName: {
    fontSize: '15px',
    fontWeight: '600' as const,
    color: '#1a1a1a',
    marginBottom: '8px',
  },
  recipeDesc: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '8px',
    lineHeight: '1.4',
  },
  recipeMeta: {
    display: 'flex',
    gap: '12px',
    fontSize: '12px',
    color: '#999',
  },
  produceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
  },
  produceCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '16px',
    backgroundColor: '#F9FAF8',
    borderRadius: '12px',
    border: '1px solid #E8EDE7',
    textDecoration: 'none',
    transition: 'all 0.2s',
  },
  produceEmoji: {
    fontSize: '24px',
    marginBottom: '8px',
  },
  produceName: {
    fontSize: '13px',
    fontWeight: '500' as const,
    color: '#1a1a1a',
    textAlign: 'center' as const,
  },
  sidebarCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    marginBottom: '24px',
  },
  sidebarTitle: {
    fontSize: '16px',
    fontWeight: '700' as const,
    color: '#1a1a1a',
    marginBottom: '16px',
  },
  actionLinks: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  actionLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: '#F9FAF8',
    borderRadius: '8px',
    color: '#1a1a1a',
    textDecoration: 'none',
    transition: 'all 0.2s',
  },
  relatedLinks: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  relatedLink: {
    padding: '10px 16px',
    backgroundColor: '#F9FAF8',
    borderRadius: '8px',
    color: '#2D5A27',
    textDecoration: 'none',
    fontSize: '14px',
    textTransform: 'capitalize' as const,
  },
  keywordTags: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
  },
  keywordTag: {
    padding: '6px 12px',
    backgroundColor: '#E8EDE7',
    borderRadius: '16px',
    color: '#2D5A27',
    textDecoration: 'none',
    fontSize: '12px',
  },
};

export default SEOPage;
